import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  Analytics,
  TrendingUp,
  TrendingDown,
  Visibility,
  ThumbUp,
  Share,
  Comment,
  ArrowBack,
  Download,
  Refresh,
  FilterList,
  Compare,
  Timeline,
  PieChart,
  BarChart,
  PersonOutline,
  Public,
  DevicesOther,
  Schedule,
  MoreVert,
  Close
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI } from '../services/analyticsAPI';
import type { 
  AnalyticsSummary, 
  PostAnalytics, 
  AdvancedAnalytics, 
  TrendAnalytics,
  PostComparison,
  ExportData
} from '../services/analyticsAPI';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const AnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [period, setPeriod] = useState('monthly');
  
  // Analytics data
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [postsAnalytics, setPostsAnalytics] = useState<any>(null);
  const [advancedAnalytics, setAdvancedAnalytics] = useState<AdvancedAnalytics | null>(null);
  const [trendsData, setTrendsData] = useState<TrendAnalytics | null>(null);
  const [comparisonData, setComparisonData] = useState<PostComparison | null>(null);
  
  // UI state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'json' | 'excel'>('csv');
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedPosts, setSelectedPosts] = useState<string[]>([]);
  const [postsPage, setPostsPage] = useState(0);
  const [postsRowsPerPage, setPostsRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    fetchAnalyticsData();
  }, [period, tabValue]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Always fetch summary
      const summaryResponse = await analyticsAPI.getSummary(period);
      setSummary(summaryResponse.data);

      // Fetch data based on selected tab
      switch (tabValue) {
        case 0: // Overview
          break;
        case 1: // Posts Analytics
          const postsResponse = await analyticsAPI.getAllPostsAnalytics({
            limit: postsRowsPerPage,
            offset: postsPage * postsRowsPerPage,
            sort_by: 'engagement_rate',
            order: 'desc'
          });
          setPostsAnalytics(postsResponse.data);
          break;
        case 2: // Advanced Analytics
          try {
            const advancedResponse = await analyticsAPI.getAdvancedAnalytics([
              'demographics', 'geographic_data', 'device_analytics', 'time_analytics'
            ]);
            setAdvancedAnalytics(advancedResponse.data);
          } catch (err: any) {
            if (err.response?.status === 403) {
              setError('Advanced analytics requires Pro+ subscription');
            } else {
              throw err;
            }
          }
          break;
        case 3: // Trends
          const trendsResponse = await analyticsAPI.getAnalyticsTrends({
            metric: 'engagement_rate',
            period: 'monthly',
            timeframe: 12
          });
          setTrendsData(trendsResponse.data);
          break;
      }
    } catch (err: any) {
      console.error('Failed to fetch analytics:', err);
      setError(err.response?.data?.detail || 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await analyticsAPI.exportAnalytics({
        format: exportFormat,
        date_range: {
          start: summary?.period.start || '',
          end: summary?.period.end || ''
        },
        metrics: ['views', 'likes', 'shares', 'comments', 'engagement_rate'],
        include_demographics: true
      });

      // Create download link
      const blob = new Blob([response.data.content], { 
        type: exportFormat === 'csv' ? 'text/csv' : 'application/json' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportDialogOpen(false);
    } catch (err: any) {
      console.error('Export failed:', err);
      setError('Failed to export analytics data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleComparePosts = async () => {
    if (selectedPosts.length < 2) {
      setError('Please select at least 2 posts to compare');
      return;
    }

    try {
      const response = await analyticsAPI.comparePostsAnalytics({
        post_ids: selectedPosts,
        metrics: ['engagement_rate', 'views', 'likes', 'shares']
      });
      setComparisonData(response.data);
    } catch (err: any) {
      console.error('Comparison failed:', err);
      setError('Failed to compare posts');
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getEngagementColor = (rate: number) => {
    if (rate >= 10) return 'success';
    if (rate >= 5) return 'warning';
    return 'error';
  };

  const renderOverviewTab = () => (
    <Grid container spacing={3}>
      {/* Key Metrics Cards */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="div" color="primary" fontWeight="bold">
                  {summary?.metrics.total_posts || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Posts
                </Typography>
                <Chip 
                  label={`${summary?.metrics.published_posts || 0} Published`} 
                  color="success" 
                  size="small" 
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography 
                  variant="h3" 
                  component="div" 
                  color={getEngagementColor(summary?.metrics.avg_engagement_rate || 0)} 
                  fontWeight="bold"
                >
                  {summary?.metrics.avg_engagement_rate?.toFixed(1) || '0.0'}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Engagement Rate
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                  <TrendingUp color="success" sx={{ fontSize: 16, mr: 0.5 }} />
                  <Typography variant="caption" color="success.main">
                    +2.1%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="div" color="info.main" fontWeight="bold">
                  {formatNumber(summary?.metrics.total_views || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Views
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                  <Visibility sx={{ fontSize: 14, mr: 0.5 }} />
                  <Typography variant="caption">
                    This period
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="div" color="success.main" fontWeight="bold">
                  {formatNumber((summary?.metrics.total_likes || 0) + (summary?.metrics.total_shares || 0))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Engagement
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                  <ThumbUp sx={{ fontSize: 14, mr: 0.5 }} />
                  <Typography variant="caption">
                    Likes + Shares
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      {/* Best Performing Post */}
      {summary?.best_performing_post && (
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Best Performing Post
            </Typography>
            <Card sx={{ bgcolor: 'success.50' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {summary.best_performing_post.topic}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip 
                    label={`${summary.best_performing_post.engagement_rate.toFixed(1)}% engagement`} 
                    color="success" 
                    size="small" 
                  />
                  <Button size="small" variant="outlined">
                    View Details
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Paper>
        </Grid>
      )}

      {/* Usage Statistics */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Usage Statistics
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              AI Generations: {summary?.usage.ai_generations || 0}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={Math.min((summary?.usage.ai_generations || 0) / 50 * 100, 100)} 
              sx={{ mt: 1, mb: 2 }}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Image Searches: {summary?.usage.image_searches || 0}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={Math.min((summary?.usage.image_searches || 0) / 100 * 100, 100)} 
              sx={{ mt: 1, mb: 2 }}
            />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Social Posts: {summary?.usage.social_posts || 0}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={Math.min((summary?.usage.social_posts || 0) / 20 * 100, 100)} 
              sx={{ mt: 1 }}
            />
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderPostsAnalyticsTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Posts Performance</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedPosts.length > 0 && (
            <>
              <Button
                startIcon={<Compare />}
                onClick={handleComparePosts}
                disabled={selectedPosts.length < 2}
              >
                Compare ({selectedPosts.length})
              </Button>
              <Button
                startIcon={<Close />}
                onClick={() => setSelectedPosts([])}
                size="small"
              >
                Clear
              </Button>
            </>
          )}
        </Box>
      </Box>

      {postsAnalytics && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Button size="small">Select</Button>
                </TableCell>
                <TableCell>Topic</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Views</TableCell>
                <TableCell align="right">Likes</TableCell>
                <TableCell align="right">Shares</TableCell>
                <TableCell align="right">Engagement Rate</TableCell>
                <TableCell align="right">Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {postsAnalytics.posts.map((post: any) => (
                <TableRow key={post.id} hover>
                  <TableCell padding="checkbox">
                    <Button
                      size="small"
                      variant={selectedPosts.includes(post.id) ? "contained" : "outlined"}
                      onClick={() => {
                        setSelectedPosts(prev => 
                          prev.includes(post.id)
                            ? prev.filter(id => id !== post.id)
                            : [...prev, post.id]
                        );
                      }}
                    >
                      {selectedPosts.includes(post.id) ? '✓' : '+'}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">{post.topic}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {post.content_preview}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={post.status} 
                      color={post.status === 'PUBLISHED' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{formatNumber(post.views)}</TableCell>
                  <TableCell align="right">{formatNumber(post.likes)}</TableCell>
                  <TableCell align="right">{formatNumber(post.shares)}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={`${post.engagement_rate.toFixed(1)}%`}
                      color={getEngagementColor(post.engagement_rate)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {new Date(post.created_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={postsAnalytics.pagination.total}
            page={postsPage}
            onPageChange={(_, newPage) => setPostsPage(newPage)}
            rowsPerPage={postsRowsPerPage}
            onRowsPerPageChange={(e) => {
              setPostsRowsPerPage(parseInt(e.target.value, 10));
              setPostsPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      )}

      {/* Post Comparison Results */}
      {comparisonData && (
        <Paper sx={{ mt: 3, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Post Comparison Results
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(comparisonData.comparison_insights).map(([metric, data]) => (
              <Grid item xs={12} md={6} key={metric}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      {metric.replace('_', ' ').toUpperCase()}
                    </Typography>
                    <Typography variant="body2">
                      Best: {data.best_performing.topic} ({data.best_performing.post_id})
                    </Typography>
                    <Typography variant="body2">
                      Worst: {data.worst_performing.topic} ({data.worst_performing.post_id})
                    </Typography>
                    <Typography variant="body2">
                      Average: {data.average.toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
    </Box>
  );

  const renderAdvancedAnalyticsTab = () => (
    <Grid container spacing={3}>
      {advancedAnalytics ? (
        <>
          {/* Demographics */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonOutline sx={{ mr: 1 }} />
                Demographics
              </Typography>
              
              <Typography variant="subtitle2" gutterBottom>Age Distribution</Typography>
              {Object.entries(advancedAnalytics.demographics.age_distribution).map(([age, percentage]) => (
                <Box key={age} sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{age}</Typography>
                    <Typography variant="body2">{percentage}%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={percentage} sx={{ mt: 0.5 }} />
                </Box>
              ))}

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Gender Distribution</Typography>
              {Object.entries(advancedAnalytics.demographics.gender_distribution).map(([gender, percentage]) => (
                <Box key={gender} sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{gender}</Typography>
                    <Typography variant="body2">{percentage}%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={percentage} sx={{ mt: 0.5 }} />
                </Box>
              ))}
            </Paper>
          </Grid>

          {/* Geographic Data */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <Public sx={{ mr: 1 }} />
                Geographic Data
              </Typography>
              
              <Typography variant="subtitle2" gutterBottom>Top Countries</Typography>
              {Object.entries(advancedAnalytics.geographic_data.countries).slice(0, 5).map(([country, percentage]) => (
                <Box key={country} sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{country}</Typography>
                    <Typography variant="body2">{percentage}%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={percentage} sx={{ mt: 0.5 }} />
                </Box>
              ))}
            </Paper>
          </Grid>

          {/* Device Analytics */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <DevicesOther sx={{ mr: 1 }} />
                Device Analytics
              </Typography>
              
              {Object.entries(advancedAnalytics.device_analytics.device_types).map(([device, percentage]) => (
                <Box key={device} sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{device}</Typography>
                    <Typography variant="body2">{percentage}%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={percentage} sx={{ mt: 0.5 }} />
                </Box>
              ))}
            </Paper>
          </Grid>

          {/* Best Posting Times */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <Schedule sx={{ mr: 1 }} />
                Best Posting Times
              </Typography>
              
              {advancedAnalytics.time_analytics.best_posting_times.slice(0, 5).map((time, index) => (
                <Box key={index} sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      {time.day} at {time.hour}:00
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      {time.engagement_rate.toFixed(1)}% engagement
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Paper>
          </Grid>
        </>
      ) : (
        <Grid item xs={12}>
          <Alert severity="info">
            Advanced analytics requires a Pro+ subscription. Upgrade your plan to access demographic insights, geographic data, and detailed audience analytics.
          </Alert>
        </Grid>
      )}
    </Grid>
  );

  const renderTrendsTab = () => (
    <Box>
      {trendsData ? (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <Timeline sx={{ mr: 1 }} />
                Engagement Rate Trends (Last 12 Months)
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Trend Direction: 
                  <Chip 
                    label={trendsData.summary.trend_direction}
                    color={trendsData.summary.trend_direction === 'upward' ? 'success' : 
                           trendsData.summary.trend_direction === 'downward' ? 'error' : 'default'}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average: {trendsData.summary.average.toFixed(1)}% | 
                  Highest: {trendsData.summary.highest.value.toFixed(1)}% ({trendsData.summary.highest.period}) |
                  Lowest: {trendsData.summary.lowest.value.toFixed(1)}% ({trendsData.summary.lowest.period})
                </Typography>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Engagement Rate</TableCell>
                      <TableCell align="right">Change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trendsData.trends.map((trend, index) => (
                      <TableRow key={index}>
                        <TableCell>{trend.period}</TableCell>
                        <TableCell align="right">{trend.value.toFixed(1)}%</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            {trend.change_percent > 0 ? (
                              <TrendingUp color="success" sx={{ fontSize: 16, mr: 0.5 }} />
                            ) : trend.change_percent < 0 ? (
                              <TrendingDown color="error" sx={{ fontSize: 16, mr: 0.5 }} />
                            ) : null}
                            <Typography 
                              variant="body2"
                              color={trend.change_percent > 0 ? 'success.main' : 
                                     trend.change_percent < 0 ? 'error.main' : 'text.secondary'}
                            >
                              {trend.change_percent > 0 ? '+' : ''}{trend.change_percent.toFixed(1)}%
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <Alert severity="info">Loading trends data...</Alert>
      )}
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/dashboard')}
              sx={{ mr: 2 }}
            >
              Back to Dashboard
            </Button>
            <Analytics color="primary" sx={{ mr: 2, fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              Analytics Dashboard
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={period}
                label="Period"
                onChange={(e) => setPeriod(e.target.value)}
              >
                <MenuItem value="weekly">Last Week</MenuItem>
                <MenuItem value="monthly">Last Month</MenuItem>
                <MenuItem value="quarterly">Last Quarter</MenuItem>
                <MenuItem value="yearly">Last Year</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              startIcon={<Download />}
              onClick={() => setExportDialogOpen(true)}
              variant="outlined"
            >
              Export
            </Button>
            
            <Button
              startIcon={<Refresh />}
              onClick={fetchAnalyticsData}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          Comprehensive analytics for your social media content performance
        </Typography>

        {summary && (
          <Typography variant="caption" color="text.secondary">
            Data for period: {new Date(summary.period.start).toLocaleDateString()} - {new Date(summary.period.end).toLocaleDateString()}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 3 }} />}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Overview" icon={<Analytics />} />
          <Tab label="Posts Analytics" icon={<BarChart />} />
          <Tab label="Advanced Analytics" icon={<PieChart />} />
          <Tab label="Trends" icon={<Timeline />} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={tabValue} index={0}>
        {renderOverviewTab()}
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        {renderPostsAnalyticsTab()}
      </TabPanel>
      
      <TabPanel value={tabValue} index={2}>
        {renderAdvancedAnalyticsTab()}
      </TabPanel>
      
      <TabPanel value={tabValue} index={3}>
        {renderTrendsTab()}
      </TabPanel>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
        <DialogTitle>Export Analytics Data</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Export Format</InputLabel>
            <Select
              value={exportFormat}
              label="Export Format"
              onChange={(e) => setExportFormat(e.target.value as any)}
            >
              <MenuItem value="csv">CSV</MenuItem>
              <MenuItem value="json">JSON</MenuItem>
              <MenuItem value="excel">Excel</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleExport} disabled={exportLoading} variant="contained">
            {exportLoading ? <CircularProgress size={20} /> : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AnalyticsPage; 